import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { cn } from '@repo/ui/lib/utils';
import { PasswordInput } from '@repo/ui/password-input';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { authClient } from '@/providers/session';
import { type SessionUser, ZCurrentPasswordSchema, ZPasswordSchema } from '@/types/auth';

export const ZPasswordFormSchema = z
  .object({
    currentPassword: ZCurrentPasswordSchema,
    password: ZPasswordSchema,
    repeatedPassword: ZPasswordSchema,
  })
  .refine((data) => data.password === data.repeatedPassword, {
    get message() {
      return t('auth.passwordsDoNotMatch');
    },
    path: ['repeatedPassword'],
  });

export type TPasswordFormSchema = z.infer<typeof ZPasswordFormSchema>;

export type PasswordFormProps = {
  className?: string;
  user: SessionUser;
};

export const PasswordForm = ({ className }: PasswordFormProps) => {
  useLocale();

  const form = useForm<TPasswordFormSchema>({
    values: {
      currentPassword: '',
      password: '',
      repeatedPassword: '',
    },
    resolver: zodResolver(ZPasswordFormSchema, { error: validationError }),
  });

  const isSubmitting = form.formState.isSubmitting;

  const onFormSubmit = async ({ currentPassword, password }: TPasswordFormSchema) => {
    try {
      await authClient.emailPassword.updatePassword({
        currentPassword,
        password,
      });

      form.reset();

      toast.success(t('auth.passwordUpdated'), {
        get description() {
          return t('auth.yourPasswordHasBeenUpdatedSuccessfully');
        },
      });
    } catch (err) {
      const error = AppError.parseError(err);

      const errorMessage = match(error.code)
        .with('NO_PASSWORD', () => t('extra.userHasNoPassword'))
        .with('INCORRECT_PASSWORD', () => t('extra.currentPasswordIsIncorrect'))
        .with('SAME_PASSWORD', () => t('extra.yourNewPasswordCannotBeTheSameAsYourOld'))
        .otherwise(() => t('extra.weEncounteredAnUnknownErrorWhileAttemptingToUpdateYour'));

      toast.error(t('auth.anErrorOccurred'), {
        description: errorMessage,
      });
    }
  };

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col gap-y-4', className)}
        onSubmit={form.handleSubmit(onFormSubmit)}
      >
        <fieldset className="flex w-full flex-col gap-y-4" disabled={isSubmitting}>
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.currentPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.newPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="repeatedPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.repeatPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
        <div className="ms-auto mt-4">
          <Button loading={isSubmitting} type="submit">
            {isSubmitting ? t('auth.updatingPassword') : t('auth.updatePassword')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
